import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from "react";
import { Button, useIafCopy } from "@inteacc/ui";
import { ApplicationState } from "./states";
const safeMessage = {
    network: { state: "offline", title: "Connection unavailable", message: "Check your connection and try again.", retryable: true },
    authorization: { state: "access-denied", title: "Access denied", message: "You are not authorized to complete this operation.", retryable: false },
    "not-found": { state: "not-found", title: "Record not found", message: "The requested resource cannot be found.", retryable: false },
    conflict: { state: "error", title: "Record changed", message: "Review the current record before retrying; your changes were not applied.", retryable: false },
    validation: { state: "error", title: "Check your input", message: "Correct the highlighted fields and try again.", retryable: false },
    "rate-limit": { state: "error", title: "Too many requests", message: "Try again later.", retryable: true },
    server: { state: "error", title: "Service temporarily unavailable", message: "The service could not complete the request.", retryable: true },
    unknown: { state: "error", title: "Unable to complete the operation", message: "Please retry or contact support with the reference ID.", retryable: true },
};
/** Maps trusted HTTP status into safe user-facing messages. Never exposes raw server details. */
export const classifyHttpFailure = (status, correlationId) => {
    const kind = status === 401 || status === 403 ? "authorization" :
        status === 404 ? "not-found" :
            status === 409 || status === 412 ? "conflict" :
                status === 400 || status === 422 ? "validation" :
                    status === 429 ? "rate-limit" :
                        status >= 500 ? "server" : "unknown";
    return { kind, ...safeMessage[kind], ...(correlationId ? { correlationId } : {}) };
};
export const classifyFailure = (error, correlationId) => {
    if (typeof error === "object" && error !== null && "status" in error &&
        typeof error.status === "number")
        return classifyHttpFailure(error.status, correlationId);
    if (error instanceof TypeError && /fetch|network|failed to fetch/i.test(error.message)) {
        return { kind: "network", ...safeMessage.network, ...(correlationId ? { correlationId } : {}) };
    }
    return { kind: "unknown", ...safeMessage.unknown, ...(correlationId ? { correlationId } : {}) };
};
export class RecoveryBoundary extends Component {
    state = { error: null };
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error, info) { this.props.onError?.(error, info); }
    componentDidUpdate(prevProps) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
            this.setState({ error: null });
        }
    }
    reset = () => {
        this.setState({ error: null });
        this.props.onReset?.();
    };
    render() {
        const { error } = this.state;
        if (!error)
            return this.props.children;
        const failure = classifyFailure(error, this.props.correlationId);
        if (this.props.fallback)
            return this.props.fallback(failure, this.reset);
        return _jsx(ApplicationState, { kind: failure.state, title: failure.title, message: failure.message, ...(failure.correlationId ? { correlationId: failure.correlationId } : {}), actionLabel: "Try again", onAction: this.reset, children: _jsx("small", { children: "Unsent changes in this section may need to be re-entered." }) });
    }
}
export const RecoveryActions = ({ failure, onRetry, onCancel, onSupport }) => {
    const t = useIafCopy();
    return _jsx(ApplicationState, { kind: failure.state, title: failure.title, message: failure.message, ...(failure.correlationId ? { correlationId: failure.correlationId } : {}), children: _jsxs("div", { className: "iaf-experience-recovery-actions", children: [failure.retryable && onRetry ? _jsx(Button, { onClick: onRetry, children: t("Retry operation") }) : null, onCancel ? _jsx(Button, { tone: "neutral", onClick: onCancel, children: t("Cancel") }) : null, onSupport ? _jsx(Button, { tone: "neutral", onClick: onSupport, children: t("Contact support") }) : null] }) });
};
//# sourceMappingURL=recovery.js.map